<template>
  <span v-if="variant === 'dot'" :class="dotBadgeClass" :title="resolvedTitle" />
  <span v-else-if="variant === 'compact'" :class="compactBadgeClass" :title="resolvedTitle">
    <component :is="statusIcon" :class="iconSizeClass" />
  </span>
  <span v-else :class="badgeClass" :title="resolvedTitle">
    <component :is="statusIcon" :class="iconSizeClass" />
    <slot>{{ textLabel }}</slot>
  </span>
</template>

<script>
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  NoSymbolIcon,
  PauseCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/20/solid'

export default {
  name: 'AppStatusBadge',
  components: {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    NoSymbolIcon,
    PauseCircleIcon,
    XCircleIcon,
  },
  props: {
    status: {
      type: String,
      default: 'unknown',
    },
    title: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'compact', 'dot'].includes(value),
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
  },
  computed: {
    normalizedStatus() {
      return (this.status || 'unknown').toLowerCase()
    },
    textLabel() {
      if (this.label) {
        return this.label
      }

      if (this.normalizedStatus === 'unknown') {
        return 'n/a'
      }

      return this.normalizedStatus
    },
    resolvedTitle() {
      return this.title ?? this.textLabel
    },
    statusIcon() {
      if (this.normalizedStatus === 'online' || this.normalizedStatus === 'valid') {
        return 'CheckCircleIcon'
      }

      if (this.normalizedStatus === 'offline' || this.normalizedStatus === 'invalid') {
        return 'NoSymbolIcon'
      }

      if (this.normalizedStatus === 'maintenance') {
        return 'InformationCircleIcon'
      }

      if (this.normalizedStatus === 'paused') {
        return 'PauseCircleIcon'
      }

      if (this.normalizedStatus === 'error') {
        return 'XCircleIcon'
      }

      return 'ExclamationTriangleIcon'
    },
    toneClass() {
      if (this.normalizedStatus === 'online' || this.normalizedStatus === 'valid') {
        return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300'
      }

      if (this.normalizedStatus === 'offline' || this.normalizedStatus === 'invalid') {
        return 'border-rose-400/30 bg-rose-500/10 text-rose-300'
      }

      if (this.normalizedStatus === 'maintenance') {
        return 'border-amber-400/30 bg-amber-500/10 text-amber-300'
      }

      if (this.normalizedStatus === 'paused') {
        return 'border-white/10 bg-white/5 text-gray-400'
      }

      if (this.normalizedStatus === 'pending') {
        return 'border-indigo-400/30 bg-indigo-500/10 text-indigo-300'
      }

      if (this.normalizedStatus === 'error') {
        return 'border-orange-400/30 bg-orange-500/10 text-orange-300'
      }

      return 'border-white/10 bg-white/5 text-gray-400'
    },
    compactToneClass() {
      if (this.normalizedStatus === 'online' || this.normalizedStatus === 'valid') {
        return 'border-emerald-400/40 bg-emerald-500/20 text-emerald-300'
      }

      if (this.normalizedStatus === 'offline' || this.normalizedStatus === 'invalid') {
        return 'border-rose-400/40 bg-rose-500/20 text-rose-300'
      }

      if (this.normalizedStatus === 'maintenance') {
        return 'border-amber-400/40 bg-amber-500/20 text-amber-300'
      }

      if (this.normalizedStatus === 'paused') {
        return 'border-white/15 bg-white/10 text-gray-300'
      }

      if (this.normalizedStatus === 'pending') {
        return 'border-indigo-400/40 bg-indigo-500/20 text-indigo-300'
      }

      if (this.normalizedStatus === 'error') {
        return 'border-orange-400/40 bg-orange-500/20 text-orange-300'
      }

      return 'border-white/15 bg-white/10 text-gray-300'
    },
    dotToneClass() {
      if (this.normalizedStatus === 'online' || this.normalizedStatus === 'valid') {
        return 'bg-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]'
      }

      if (this.normalizedStatus === 'offline' || this.normalizedStatus === 'invalid') {
        return 'bg-rose-400 shadow-[0_0_0_3px_rgba(251,113,133,0.12)]'
      }

      if (this.normalizedStatus === 'maintenance') {
        return 'bg-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.12)]'
      }

      if (this.normalizedStatus === 'pending') {
        return 'bg-indigo-400 shadow-[0_0_0_3px_rgba(129,140,248,0.12)]'
      }

      if (this.normalizedStatus === 'error') {
        return 'bg-orange-400 shadow-[0_0_0_3px_rgba(251,146,60,0.12)]'
      }

      return 'bg-gray-500 shadow-[0_0_0_3px_rgba(107,114,128,0.12)]'
    },
    iconSizeClass() {
      return {
        xs: 'size-2.5',
        sm: 'size-3',
        md: 'size-3',
        lg: 'size-3.5',
        xl: 'size-4',
      }[this.size]
    },
    badgeSizeClass() {
      return {
        xs: 'gap-1 rounded px-1 py-0.5 text-[10px]',
        sm: 'gap-1 rounded-md px-1.5 py-0.5 text-xxs',
        md: 'gap-1 rounded-md px-1.5 py-0.5 text-xxs',
        lg: 'gap-1.5 rounded-md px-2 py-1 text-xs',
        xl: 'gap-2 rounded-md px-2.5 py-1 text-xs',
      }[this.size]
    },
    compactSizeClass() {
      return {
        xs: 'size-4',
        sm: 'size-5',
        md: 'size-6',
        lg: 'size-7',
        xl: 'size-8',
      }[this.size]
    },
    dotSizeClass() {
      return {
        xs: 'size-1',
        sm: 'size-1.5',
        md: 'size-2',
        lg: 'size-2.5',
        xl: 'size-3',
      }[this.size]
    },
    badgeClass() {
      return [
        'inline-flex items-center border font-semibold uppercase tracking-wide whitespace-nowrap',
        this.badgeSizeClass,
        this.toneClass,
      ]
    },
    compactBadgeClass() {
      return [
        'inline-flex items-center justify-center rounded-full border shadow-sm',
        this.compactSizeClass,
        this.compactToneClass,
      ]
    },
    dotBadgeClass() {
      return [
        'inline-flex shrink-0 rounded-full',
        this.dotSizeClass,
        this.dotToneClass,
      ]
    },
  },
}
</script>
